/**
 * Audio Utilities — Smoothing, beat response patterns, and helpers for visualizations.
 */

/**
 * Smoothed value that lerps toward a target. Use for any audio-driven value
 * to prevent visual jitter.
 *
 * Usage:
 *   const myValue = new SmoothedValue(0.1);  // smoothing factor
 *   // In render loop:
 *   myValue.update(frame.bassEnergy);
 *   const smooth = myValue.value;
 */
export class SmoothedValue {
  value = 0;
  constructor(private factor: number = 0.15) {}

  update(target: number): number {
    this.value += (target - this.value) * this.factor;
    return this.value;
  }

  reset(value = 0): void {
    this.value = value;
  }
}

/**
 * Beat pulse — spikes to 1.0 on beat, then decays exponentially.
 * Common pattern for scaling sizes, brightness, speed on beats.
 *
 * Usage:
 *   const pulse = new BeatPulse(0.93);  // decay rate
 *   // In render loop:
 *   pulse.update(frame.isBeat, frame.beatIntensity);
 *   const size = baseSize + pulse.value * extraSize;
 */
export class BeatPulse {
  value = 0;
  constructor(private decay: number = 0.93) {}

  update(isBeat: boolean, intensity = 1): number {
    if (isBeat) {
      this.value = intensity;
    }
    this.value *= this.decay;
    return this.value;
  }
}
